// src/pages/app-users/AppUserProfilePage.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "components/ui/Button";
import { Modal } from "components/ui/Modal";
import { AssignModal } from "components/assignments/AssignModal";
import { ReturnModal } from "components/assignments/ReturnModal";
import {
  getAppUser,
  getAppUserAssignments,
  getAppUserActs,
  updateAppUser,
} from "api/appUsers";
import ActDetailsModal from "./ActDetailsModal";
import QRCode from "qrcode";
import { generateHistoryPdf } from "lib/pdf/generateHistoryPdf";

/* --- Download History Helper --- */
const downloadHistory = (acts, user) => {
  if (!acts || acts.length === 0) {
    alert('Yuklab olish uchun ma\'lumot yo\'q');
    return;
  }

  // Create CSV content
  const headers = ['Sana', 'Dalolatnoma turi', 'Akt №', 'Mahsulot', 'Artikul', 'Miqdor', 'Narx', 'Umumiy qiymat'];
  const rows = [];

  acts.forEach(act => {
    const actType = { in: 'Berish', return: 'Qaytarish', out: 'Hisobdan chiqarish' }[act.act_type] || act.act_type;
    const date = new Date(act.created_at).toLocaleDateString('ru-RU');

    if (act.items && act.items.length > 0) {
      act.items.forEach(item => {
        const qty = item.qty || 0;
        const price = item.price || 0;
        const total = qty * price;

        rows.push([
          date,
          actType,
          act.act_no || '-',
          item.item_name || '-',
          item.item_sku || '-',
          qty,
          price,
          total
        ]);
      });
    } else {
      rows.push([date, actType, act.act_no || '-', '-', '-', '-', '-', '-']);
    }
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  // Download
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `history_${user?.full_name || 'user'}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

/* --- Icons (Consistent 1.5px Stroke) --- */
const Icons = {
  Back: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  ),
  Plus: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  ),
  Return: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 14 4 9l5-5" />
      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
    </svg>
  ),
  Building: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M8 10h.01" />
      <path d="M16 10h.01" />
      <path d="M8 14h.01" />
      <path d="M16 14h.01" />
    </svg>
  ),
  Phone: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Box: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  Download: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Copy: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  History: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
    </svg>
  ),
  Camera: (p) => (
    <svg
      {...p}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
};

/* --- Helpers --- */
const initials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "??";
const buildProfileUrl = (base, uid) =>
  `${String(base || "").replace(/\/+$/, "")}/users/${uid}`;

/* --- Components --- */

// 1. Skeleton Loader (Better UX than spinner)
const SkeletonProfile = () => (
  <div className="max-w-6xl mx-auto p-6 space-y-8 animate-pulse">
    <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-3xl w-full"></div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8 space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-1/4 mb-4"></div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-gray-200 dark:bg-slate-700 rounded-2xl"></div>
        ))}
      </div>
      <div className="lg:col-span-4 space-y-4">
        <div className="h-64 bg-gray-200 dark:bg-slate-700 rounded-2xl"></div>
        <div className="h-40 bg-gray-200 dark:bg-slate-700 rounded-2xl"></div>
      </div>
    </div>
  </div>
);

// 2. Profile Header
const ProfileHeader = ({ user, stats, onAssign, onEditPhoto }) => {
  const [copied, setCopied] = useState(false);
  const badge = user.barcode || `ID-${user.id}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(badge);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] p-6 sm:p-8 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] dark:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-slate-700 relative overflow-hidden">
      {/* Subtle Texture */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: "radial-gradient(#111 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      ></div>

      <div className="relative flex flex-col md:flex-row gap-8 items-start">
        {/* Avatar */}
        <div className="relative shrink-0 group">
          <div className="h-28 w-28 rounded-full p-1.5 bg-white dark:bg-slate-700 border border-gray-100 dark:border-slate-600 shadow-sm">
            <div className="h-full w-full rounded-full bg-slate-50 dark:bg-slate-600 flex items-center justify-center overflow-hidden text-2xl font-bold text-slate-400 dark:text-slate-300">
              {(user.photo_url || user.avatar_url) ? (
                <img
                  src={user.photo_url || user.avatar_url}
                  className="w-full h-full object-cover"
                  alt="avatar"
                  onError={(e) => {
                    // Если фото не загрузилось, показываем инициалы
                    e.target.style.display = "none";
                    const parent = e.target.parentElement;
                    if (parent && !parent.querySelector("span")) {
                      const span = document.createElement("span");
                      span.textContent = initials(user.full_name);
                      parent.appendChild(span);
                    }
                  }}
                />
              ) : (
                initials(user.full_name)
              )}
            </div>
          </div>
          <div className="absolute bottom-2 right-2 bg-white p-0.5 rounded-full shadow-sm">
            <div className="h-4 w-4 bg-emerald-500 rounded-full border-[3px] border-white"></div>
          </div>
          {/* Кнопка редактирования фото */}
          {onEditPhoto && (
            <button
              onClick={onEditPhoto}
              className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              title="Rasmni o'zgartirish"
            >
              <div className="bg-white rounded-full p-2 shadow-lg">
                <Icons.Camera className="w-5 h-5 text-gray-700" />
              </div>
            </button>
          )}
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0 pt-2">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 tracking-tight">
              {user.full_name}
            </h1>
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-slate-400 mb-6">
            <span className="font-medium text-gray-900 dark:text-slate-100">
              {user.role || "Xodim"}
            </span>
            <span className="h-1 w-1 rounded-full bg-gray-300"></span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors group"
            >
              <span className="font-mono">{badge}</span>
              {copied ? (
                <span className="text-xs text-green-600 font-medium ml-1">
                  Nusxalandi
                </span>
              ) : (
                <Icons.Copy className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </button>
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-slate-700 border border-gray-200/50 dark:border-slate-600/50 text-xs font-medium text-gray-600 dark:text-slate-300">
              <Icons.Building className="w-3.5 h-3.5 text-gray-400" />
              {user.department || "Bo'lim yo'q"}
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200/50 text-xs font-medium text-gray-600">
              <span className="text-gray-400 font-bold">#</span>
              {user.room_no ? `Xona ${user.room_no}` : "Xona —"}
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200/50 text-xs font-medium text-gray-600">
              <Icons.Phone className="w-3.5 h-3.5 text-gray-400" />
              <span className="font-mono">{user.phone || "—"}</span>
            </div>
          </div>
        </div>

        {/* Stats & CTA */}
        <div className="flex flex-row md:flex-col gap-6 items-center md:items-end w-full md:w-auto border-t md:border-t-0 md:border-l border-gray-100 dark:border-slate-700 pt-6 md:pt-2 md:pl-8">
          <div className="flex gap-8 text-center md:text-right mr-auto md:mr-0">
            <div className="group cursor-default">
              <div className="text-2xl font-bold text-gray-900 dark:text-slate-100 tabular-nums leading-none group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {stats.assignments}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mt-1.5">
                Mahsulot
              </div>
            </div>
            <div className="group cursor-default">
              <div className="text-2xl font-bold text-gray-900 tabular-nums leading-none group-hover:text-blue-600 transition-colors">
                {stats.acts}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mt-1.5">
                Dalolatnoma
              </div>
            </div>
          </div>

          <Button
            onClick={onAssign}
            className="bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 dark:hover:bg-slate-600 text-white rounded-xl p-2.5 shadow-lg shadow-slate-200/60 dark:shadow-slate-900/60 active:scale-95 transition-all"
            title="Mahsulot berish"
          >
            <Icons.Plus className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// 3. Inventory Item
const InventoryRow = ({ item, onReturn }) => (
  <div className="group relative flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-600 hover:shadow-[0_4px_20px_-10px_rgba(37,99,235,0.1)] dark:hover:shadow-[0_4px_20px_-10px_rgba(59,130,246,0.3)] transition-all duration-300">
    <div className="flex items-center gap-5 flex-1 min-w-0">
      <div className="h-12 w-12 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-400 group-hover:bg-blue-50 dark:group-hover:bg-blue-900 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex items-center justify-center border border-slate-100 dark:border-slate-600 transition-colors shrink-0">
        <Icons.Box className="w-6 h-6" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors truncate" title={item.item_name}>
          {item.item_name}
        </h3>
        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
          <span className="bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium text-gray-600 dark:text-slate-400">
            {new Date(item.created_at).toLocaleDateString()}
          </span>
          <span className="text-gray-300 dark:text-slate-600">•</span>
          <span className="font-mono text-gray-400 dark:text-slate-500">#{item.id}</span>
        </div>
      </div>
    </div>

    <div className="flex items-center gap-4 sm:gap-6 pl-2 shrink-0">
      <div className="text-right">
        <span className="block text-lg font-bold text-gray-900 dark:text-slate-100 leading-none tabular-nums">
          {Math.max(0, item.qty - (item.qty_returned || 0))}
        </span>
        <span className="text-[10px] uppercase font-bold text-gray-400 dark:text-slate-500 tracking-wider">
          {item.item_unit}
        </span>
      </div>

      <div className="h-8 w-px bg-gray-100 hidden sm:block"></div>

      <button
        onClick={onReturn}
        className="flex items-center justify-center gap-2 text-xs font-semibold text-gray-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-gray-50 dark:bg-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 px-4 py-2 rounded-lg transition-colors"
        title="Qaytarish"
      >
        <Icons.Return className="w-4 h-4" />
        <span className="hidden sm:inline">Qaytarish</span>
      </button>
    </div>
  </div>
);

// 4. Compact ID Card Widget
const IDCardWidget = ({ url, badge }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (url && ref.current) {
      QRCode.toCanvas(ref.current, url, {
        width: 140,
        margin: 0,
        color: { dark: "#1e293b", light: "#ffffff" },
      });
    }
  }, [url]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.download = `ID_${badge}.png`;
    a.href = ref.current.toDataURL();
    a.click();
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm">
      <div className="flex flex-col items-center">
        <div className="w-full flex justify-between items-center mb-4 border-b border-gray-100 pb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
            Digital Pass
          </span>
          <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
        </div>

        <div className="p-2 bg-white rounded-xl border border-gray-100 shadow-inner mb-4">
          <canvas ref={ref} className="block w-32 h-32 opacity-90" />
        </div>

        <div className="font-mono text-sm font-bold text-gray-700 bg-gray-50 px-3 py-1 rounded-md mb-4">
          {badge}
        </div>

        <button
          onClick={handleDownload}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-xs font-medium transition-colors"
        >
          <Icons.Download className="w-3.5 h-3.5" /> PNG yuklab olish
        </button>
      </div>
    </div>
  );
};

// 5. Timeline Item
const TimelineItem = ({ act, onClick }) => {
  const isReturn = act.act_type === "return";
  const isIn = act.act_type === "in";

  const styles = isIn
    ? {
      dot: "bg-blue-500 ring-blue-100",
      label: "Berish",
      text: "text-blue-700",
    }
    : isReturn
      ? {
        dot: "bg-emerald-500 ring-emerald-100",
        label: "Qaytarish",
        text: "text-emerald-700",
      }
      : {
        dot: "bg-amber-500 ring-amber-100",
        label: "Hisobdan chiqarish",
        text: "text-amber-700",
      };

  return (
    <div className="relative pl-6 pb-6 last:pb-0">
      <div className="absolute left-[7px] top-3 bottom-0 w-px bg-gray-200 last:hidden"></div>
      <div
        className={`absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full ring-4 ${styles.dot}`}
      ></div>

      <div
        onClick={() => onClick(act)}
        className="cursor-pointer -mt-1 p-2 -ml-2 rounded-lg hover:bg-gray-50 transition-colors group"
      >
        <div className="flex justify-between items-baseline">
          <span className={`text-xs font-bold ${styles.text}`}>
            {styles.label}
          </span>
          <span className="text-[10px] text-gray-400 font-mono">
            {new Date(act.created_at).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">
            Akt <span className="font-mono">#{act.act_no}</span>
          </span>
          <Icons.Back className="w-3 h-3 text-gray-300 rotate-180 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </div>
  );
};

/* --- Main Page --- */
export default function AppUserProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ user: null, assignments: [], acts: [] });
  const [loading, setLoading] = useState(true);
  const [modals, setModals] = useState({
    assign: false,
    return: false,
    act: null,
    editPhoto: false,
  });
  const [selectedAsg, setSelectedAsg] = useState(null);
  const [actFilters, setActFilters] = useState({
    act_type: '',
    date_from: '',
    date_to: '',
  });

  const refresh = useCallback(async () => {
    try {
      const [u, asg] = await Promise.all([
        getAppUser(id),
        getAppUserAssignments(id),
      ]);
      setData(prev => ({ ...prev, user: u, assignments: asg || [] }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchActs = useCallback(async () => {
    try {
      const params = {};
      if (actFilters.act_type) params.act_type = actFilters.act_type;
      if (actFilters.date_from) params.date_from = actFilters.date_from;
      if (actFilters.date_to) params.date_to = actFilters.date_to;

      const acts = await getAppUserActs(id, params);
      setData(prev => ({ ...prev, acts: acts || [] }));
    } catch (e) {
      console.error('Failed to fetch acts:', e);
    }
  }, [id, actFilters]);

  useEffect(() => {
    refresh();
    fetchActs();
  }, [refresh, fetchActs]);

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate("/app-users");
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center">
        <SkeletonProfile />
      </div>
    );
  if (!data.user)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] text-gray-400 font-medium">
        Foydalanuvchi topilmadi
      </div>
    );

  const profileUrl = buildProfileUrl(
    process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin,
    id
  );
  const badge =
    data.user.barcode || `ID-${String(data.user.id).padStart(6, "0")}`;

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-900 font-sans text-gray-900 dark:text-slate-100 pb-20 selection:bg-blue-100 selection:text-blue-900">
      {/* Sticky Navbar with Blur */}
      <nav className="sticky top-0 z-40 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border-b border-gray-200/80 dark:border-slate-700/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="group flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 hover:bg-gray-100/50 dark:hover:bg-slate-700/50 transition-all"
          >
            <Icons.Back className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Ro'yxatga qaytish
          </button>
          <div className="hidden sm:flex items-center gap-2 text-sm text-gray-400">
            <span>Xodimlar</span>
            <span>/</span>
            <span className="text-gray-700 font-medium truncate max-w-[150px]">
              {data.user.full_name}
            </span>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Profile Hero */}
        <ProfileHeader
          user={data.user}
          stats={{
            assignments: data.assignments.length,
            acts: data.acts.length,
          }}
          onAssign={() => setModals({ ...modals, assign: true })}
          onEditPhoto={() => setModals({ ...modals, editPhoto: true })}
        />

        {/* Content Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative">
          {/* Main Content (Inventory) - 8 Cols */}
          <div className="lg:col-span-8 space-y-6">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">
                Biriktirilgan mahsulotlar
              </h2>
              {data.assignments.length > 0 && (
                <span className="bg-white border border-gray-200 text-gray-600 px-2.5 py-0.5 rounded-md text-xs font-bold shadow-sm">
                  {data.assignments.length}
                </span>
              )}
            </div>

            {data.assignments.length === 0 ? (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-slate-700 p-12 text-center">
                <div className="mx-auto w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-4">
                  <Icons.Box className="w-6 h-6 opacity-50" />
                </div>
                <p className="text-gray-500 font-medium mb-4">
                  Faol topshiriqlar yo'q
                </p>
                <Button
                  onClick={() => setModals({ ...modals, assign: true })}
                  variant="outline"
                  size="sm"
                  className="bg-white"
                >
                  Birinchi mahsulotni berish
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {data.assignments.map((a) => (
                  <InventoryRow
                    key={a.id}
                    item={a}
                    onReturn={() => {
                      setSelectedAsg(a);
                      setModals({ ...modals, return: true });
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sticky Sidebar (QR + History) - 4 Cols */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <IDCardWidget url={profileUrl} badge={badge} />

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <Icons.History className="w-3.5 h-3.5" />
                  Operatsiyalar tarixi
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => downloadHistory(data.acts, data.user)}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Icons.Download className="w-3.5 h-3.5" />
                    CSV
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => generateHistoryPdf(data.acts, data.user)}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Icons.Download className="w-3.5 h-3.5" />
                    PDF
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                <select
                  value={actFilters.act_type}
                  onChange={(e) => setActFilters({ ...actFilters, act_type: e.target.value })}
                  className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md"
                >
                  <option value="">Barcha turlar</option>
                  <option value="in">Berish</option>
                  <option value="return">Qaytarish</option>
                  <option value="out">Hisobdan chiqarish</option>
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={actFilters.date_from}
                    onChange={(e) => setActFilters({ ...actFilters, date_from: e.target.value })}
                    placeholder="Dan"
                    className="text-xs px-2 py-1.5 border border-gray-200 rounded-md"
                  />
                  <input
                    type="date"
                    value={actFilters.date_to}
                    onChange={(e) => setActFilters({ ...actFilters, date_to: e.target.value })}
                    placeholder="Gacha"
                    className="text-xs px-2 py-1.5 border border-gray-200 rounded-md"
                  />
                </div>
              </div>

              {data.acts.length === 0 ? (
                <div className="text-center text-xs text-gray-400 py-6 italic">
                  {actFilters.act_type || actFilters.date_from || actFilters.date_to
                    ? 'Tanlangan filtrlar bo\'yicha dalolatnomalar yo\'q'
                    : 'Tarix bo\'sh'}
                </div>
              ) : (
                <div className="mt-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {data.acts.map((act) => (
                    <TimelineItem
                      key={act.id}
                      act={act}
                      onClick={(a) => setModals({ ...modals, act: a })}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssignModal
        open={modals.assign}
        onClose={() => setModals({ ...modals, assign: false })}
        onCreated={refresh}
        fixedUserId={id}
      />
      <ReturnModal
        open={modals.return}
        onClose={() => setModals({ ...modals, return: false })}
        assignment={selectedAsg}
        onReturned={refresh}
      />
      <ActDetailsModal
        open={!!modals.act}
        act={modals.act}
        onClose={() => setModals({ ...modals, act: null })}
        user={data.user}
        typeLabel={(t) =>
          ({ in: "Berish", out: "Hisobdan chiqarish", return: "Qaytarish" }[t] || t)
        }
      />
      <EditPhotoModal
        open={modals.editPhoto}
        user={data.user}
        onClose={() => setModals({ ...modals, editPhoto: false })}
        onUpdated={refresh}
      />
    </div>
  );
}

/* --- Edit Photo Modal --- */
const EditPhotoModal = ({ open, user, onClose, onUpdated }) => {
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPhoto(null);
      setPhotoPreview(null);
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Проверка типа файла
      if (!file.type.startsWith('image/')) {
        setError("Пожалуйста, выберите изображение");
        return;
      }
      // Проверка размера (макс 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Размер изображения не должен превышать 5MB");
        return;
      }
      setPhoto(file);
      // Создаем превью
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleSave = async () => {
    if (!photo) {
      setError("Пожалуйста, выберите новое фото");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateAppUser(user.id, { photo });
      onUpdated?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "Не удалось обновить фото");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Вы уверены, что хотите удалить фото?")) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Отправляем пустую строку для удаления фото
      await updateAppUser(user.id, { photo: '' });
      onUpdated?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "Не удалось удалить фото");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Изменить фото профиля"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button
            variant="ghost"
            onClick={handleRemove}
            disabled={saving || (!user.photo_url && !user.avatar_url)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            Удалить фото
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !photo}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? "Сохранение..." : "Сохранить"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Текущее фото / Предпросмотр */}
        {(user.photo_url || user.avatar_url || photoPreview) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {photoPreview ? "Предпросмотр" : "Текущее фото"}
            </label>
            <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-gray-300 mx-auto">
              <img
                src={photoPreview || user.photo_url || user.avatar_url}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Поле загрузки */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Выберите новое фото
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <p className="text-xs text-gray-500 mt-1">Максимальный размер: 5MB</p>
        </div>

        {error && (
          <p className="text-sm text-red-600 text-center pt-2">{error}</p>
        )}
      </div>
    </Modal>
  );
};
